import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { authService } from '../services/authService.js';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { usePageTitle } from '../hooks/usePageTitle.js';
import toast from 'react-hot-toast';
import './EmailVerification.css';

const RESEND_COOLDOWN = 60;

function EmailVerification() {
  usePageTitle('Verificação de E-mail');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserFromStorage } = useAuth();

  const emailFromState = location.state?.email;
  const email = emailFromState || user?.email || '';

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!user && !emailFromState) {
      navigate('/', { replace: true });
      return;
    }
    if (user && user.emailVerified !== false) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, emailFromState, navigate]);

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    if (error) setError('');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Digite o código de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.verifyEmail(email, code);
      refreshUserFromStorage();
      toast.success('E-mail verificado com sucesso!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Código inválido ou expirado');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      await authService.resendVerificationEmail(email);
      toast.success('Novo código enviado para seu e-mail');
      startResendCooldown();
    } catch (err) {
      toast.error(err.message || 'Erro ao reenviar o código');
    }
  };

  return (
    <div className="ev-container">
      <div className="ev-background"></div>
      <div className="ev-card">
        <div className="ev-header">
          <div className="ev-logo-wrapper">
            <Logo className="ev-logo-svg" height="70px" />
          </div>
          <h2 className="ev-title">Verificação de e-mail</h2>
          <p className="ev-subtitle">
            Enviamos um código de 6 dígitos para{' '}
            <strong>{email}</strong>.{' '}
            Insira o código abaixo para confirmar seu cadastro.
          </p>
        </div>

        <form onSubmit={handleVerify} className="ev-form">
          <div className="ev-input-group">
            <label htmlFor="code">Código de verificação</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength={6}
              disabled={isLoading}
              className="ev-code-input"
            />
          </div>

          {error && <div className="ev-error">{error}</div>}

          <button
            type="submit"
            className="ev-button"
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? <div className="ev-spinner"></div> : 'Verificar e-mail'}
          </button>
        </form>

        <div className="ev-footer">
          <p className="ev-resend-text">Não recebeu o código?</p>
          <button
            type="button"
            className="ev-resend-button"
            onClick={handleResend}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `Reenviar em ${resendCooldown}s`
              : 'Reenviar código'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
